import React from "react"
import App from "next/app"
import { NextIntlClientProvider } from "next-intl"
import { Provider, useStaticRendering } from "mobx-react"
import { getSnapshot } from "mobx-state-tree"
import { ThemeProvider } from "styled-components"
import { parseCookies } from "nookies"
import { DefaultSeo } from "next-seo"
import Modal from "react-modal"
import smoothscroll from "smoothscroll-polyfill"
import Head from "next/head"
import dynamic from "next/dynamic"
import {
  MOBILE_SIZE,
  TABLET_SIZE,
  SMALL_DESKTOP_SIZE,
  PAYMENT_PROVIDERS,
} from "~/utilities/constants"

import theme from "~/style/theme"
import GlobalStyle from "~/style/GlobalStyle"

import * as UserAgent from "~/utilities/userAgent"
import * as LocalStorage from "~/utilities/localStorage"
import { isSameUtm, resolveAttribution, collectAttributionFromQuery, readQueryFromLocation } from "~/utilities/adAttribution"
import { captureLandingPage, getLanding } from "~/utilities/landingPage"
import i18nLoader from "~/utilities/i18nLoader"

import Toasts from "~/components/Toasts"
import Intercom from "~/components/_integrations/Intercom"

import { magicLinkSignIn } from "~/services/auth"
import { forwardedClientHeaders } from "~/utilities/forwardedClientHeaders"
import { initMetaCapiContext } from "~/utilities/metaCapiContext"
import { initStore } from "~/stores/AppStore"
import { initTrackhome, initPostHog, identifyUser } from "~/services/tracking"

import SEO from "../next-seo.config"

import "~/style/GlobalStyle/fonts.css"
import "font-awesome/css/font-awesome.min.css"
import "~/components/Toasts/toasts.css"
import "~/components/sliders/mobileSwiperStyle.css"
import "~/components/traffic-tickets/Carousel/Slick/swiper.css"
import "~/components/SendbirdChat/sendbird.css"

import IndexComponent from "~/pages/index"
import {
  getChargeRequestStatusAfter3DSChallengeForCko,
  getPersistedApplicationState,
} from "~/services/payments"

import Clarity from "~/components/_integrations/Clarity"
import CookieBot from "~/components/cookies/CookieBot"
const BlockedModal = dynamic(() => import("~/components/auth/BlockedModal"))
const DeletedModal = dynamic(() => import("~/components/auth/DeletedModal"))
const UserMissingInfoModal = dynamic(() =>
  import("~/components/auth/UserMissingInfoModal")
)
const PhoneVerificationModal = dynamic(() =>
  import("~/components/auth/PhoneVerificationModal")
)
const MagicLinkHandler = dynamic(() =>
  import("~/components/auth/MagicLinkHandler")
)
const AuthReturnStateHandler = dynamic(() =>
  import("~/components/auth/AuthReturnStateHandler")
)
const GoogleOneTap = dynamic(() =>
  import("~/components/auth/GoogleOneTap")
)

require("setimmediate")

const isServer = typeof window === "undefined"
// prevent SSR memory leaks due to observer not changing on the server
useStaticRendering(isServer)

// React Modal Configuration
Modal.setAppElement("#__next")
Modal.defaultStyles = {}

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    const isServer = !!ctx.req
    const sessionToken = parseCookies(ctx).sessionToken || ctx.query.s
    const CKOsessionId = ctx.query["cko-session-id"]
    const { paymentResult, flowType, objectId } = ctx.query

    let initialStateResult
    if (CKOsessionId && paymentResult) {
      initialStateResult = await getPersistedApplicationState(CKOsessionId)
    }

    const store = initStore(isServer, initialStateResult)

    // Init Mobile and Webkit State (prefill state with this when sent to client)
    if (isServer) {
      const userAgentHeader = ctx.req.headers["user-agent"]
      const currentHost = ctx.req.headers.host
      store.ui.setMobile(UserAgent.isMobile(userAgentHeader))
      store.ui.setWebkit(UserAgent.isWebkit(userAgentHeader))
      store.ui.setCurrentHost(currentHost)
    }

    // Magic Link sign in if not signed in and is on server
    if (ctx.query.mlt && ctx.query.mlu && !store.auth.isLoggedIn && isServer) {
      try {
        const user = await magicLinkSignIn(ctx.query.mlu, ctx.query.mlt, forwardedClientHeaders(ctx.req))
        if (user) {
          store.auth.setUser(user, ctx)
        }
      } catch (error) {
        console.error("Error signing in a user with magic link", error)
      }
    }

    // Auto sign in a user from a cookie or s query param
    if (sessionToken && !store.auth.isLoggedIn && isServer) {
      await store.auth.sessionTokenLogin(sessionToken, ctx)
    }

    if (!isServer && LocalStorage.get("sessionToken")) {
      LocalStorage.remove("sessionToken")
    }

    const messages = await i18nLoader(ctx.locale)

    // Capture host + path so canonical/og:url can self-reference per request
    // host instead of pointing at the hardcoded appwinit.com domain.
    let host = ctx.req?.headers?.host
    if (!host && typeof window !== "undefined") {
      host = window.location.host
    }
    const seoPath = (ctx.asPath || "").split("?")[0]

    // Page render
    let pageProps = {}
    if (Component.getInitialProps) {
      ctx.store = store
      pageProps = await Component.getInitialProps(ctx)
    }

    let initialState
    if (initialStateResult) {
      initialState = initialStateResult
      initialState.auth.cameFromPersistedState = true
    } else {
      initialState = getSnapshot(store)
    }

    return {
      pageProps,
      initialState,
      isServer,
      pathname: ctx.pathname,
      isMobileTrafficPayFlow: ctx.query.isWebMobile === 'true',
      flowType,
      paymentResult,
      CKOsessionId,
      objectId,
      messages,
      host,
      seoPath,
    }
  }

  constructor(props) {
    super(props)
    this.store = initStore(props.isServer, props.initialState)
  }

  componentDidMount = async () => {
    if (process.browser) {
      initTrackhome()
      initPostHog()
      // Bind GA4 user_id (+ trackhome) on the client after every hydration so
      // logged-in sessions restored from a cookie via SSR sessionTokenLogin
      // (which ran setUser on the server where gtag is unavailable) still get
      // a user_id bound for cross-device + LTV reporting. Idempotent with the
      // binding that setUser already performed for fresh client-side logins.
      if (this.store.auth.isLoggedIn && this.store.auth.user?.objectId) {
        identifyUser(this.store.auth.user.objectId)
      }
      smoothscroll.polyfill()

      this.store.router.startRouterWatch() // subscribe router events to store
      this.store.intercom.startIntercomAgent() // bootup intercom gate keeper

      this.windowResizeHandler() // init browser check
      // watch for resize events to keep client in state
      window.addEventListener("resize", this.windowResizeHandler)

      const { isLoggedIn } = this.store.auth

      // An explicit query param can be set to track if a user arrives from a given email, etc
      const userOriginQueryParam = this.store.router.query.origin
      if (isLoggedIn && userOriginQueryParam) {
        this.store.auth.user.setOrigin(userOriginQueryParam)
      }

      if (!isLoggedIn) {
        // if there is no user, they shouldnt have sessionToken stored
        this.store.auth.unsetUser()
      }
      captureLandingPage()
      // Look for utm query parameters when the user first arrives
      this.handleUtmParameters(isLoggedIn)

      if (window.location.host.includes('trafficviolations.info')) {
        initMetaCapiContext()
      }

      if (
        this.props.CKOsessionId &&
        ["PARKING_PAY", "DASHBOARD_PARKING_PAY"].includes(this.props.flowType)
      ) {
        const userSession = this.props.initialState.auth.user.sessionToken
        const userEmail = this.props.initialState.auth.user.email
        const ckoSessionId = this.props.CKOsessionId
        const { flowType } = this.props
        setTimeout(async () => {
          const resultError = await getChargeRequestStatusAfter3DSChallengeForCko(
            userSession,
            userEmail,
            ckoSessionId,
            flowType
          )
          if (resultError) {
            this.store.payment.showErrorToast(resultError, 10000)
          }
        }, 2000)
      }

      // Restore any missing volatile data on the client-side not included in persisted initial state
      if (this.store.auth.cameFromPersistedState) {
        let step
        if (this.props.flowType !== "DASHBOARD_PARKING_PAY") {
          const stepMap = {
            PARKING_FIGHT: {
              cancel: "payment",
              failure: "payment",
              success: "success",
            },
            PARKING_PAY: {
              cancel: "pay",
              failure: "pay",
              success: "success",
            },
            // Todo:
            TRAFFIC_SUBMISSION: {
              cancel: "tbd",
              failure: "tbd",
              success: "tbd",
            },
            TRAFFIC_LOOKUP: {
              cancel: "tbd",
              failure: "tbd",
              success: "tbd",
            },
          }
          step = stepMap[this.props.flowType][this.props.paymentResult]
        }
        switch (this.props.flowType) {
          case "DASHBOARD_PARKING_PAY":
            this.props.router.push(
              `/dashboard/parking?objectId=${this.props.objectId}`
            )
            break
          case "PARKING_FIGHT":
            this.store.parkingFlow.flow.setType("fight", step)
            this.props.router.push("/parking/flow")
            break
          case "PARKING_PAY":
            this.store.parkingFlow.flow.setType("pay", step)
            this.props.router.push("/parking/flow")
            break
          case "TRAFFIC_SUBMISSION":
            // tbd
            break
          case "TRAFFIC_LOOKUP":
            // tbd
            break
          default:
            console.warn("Came from persistedState with undefined flowtype")
        }
      }

      if (window.clarity) {
        window.clarity("identify", this.store.auth.user.objectId)
      }

      // Parking pay via Evervault encrypts the card client-side and charges
      // through /evervault/* — it never calls the phone-gated getEphemeralKey,
      // so phone verification is not required for that flow. Skip the
      // auto-open so the modal never blocks Evervault parking pay. Other
      // processors (Braintree/Stripe) still tokenize via getEphemeralKey and
      // keep the phone-verification gate, so the modal stays for them.
      const isParkingPayEvervault =
        (this.store.parkingFlow.flow.isPayFlow ||
          this.store.parkingFlow.flow.isRobotPayFlow) &&
        this.store.payment.paymentProcessor === PAYMENT_PROVIDERS.evervault
      if (
        isLoggedIn &&
        this.store.auth.user.phoneNumber &&
        this.store.auth.user.phoneNumber !== "" &&
        !this.store.auth.user.phoneNumberVerified &&
        !this.store.payment.isLoading &&
        !isParkingPayEvervault
      ) {
        this.store.ui.setPhoneVerifyModalShowing(true)
      }
      const {target: ticketObjectIds} = this.store.router.query
      const isPayParkingFromMobile = this.props.router.pathname.includes(
          "/parking/pay"
      ) && ticketObjectIds
      if (isLoggedIn && isPayParkingFromMobile) {
        this.store.parkingFlow.flow.setIsPayParkingFromMobile(true)
        await this.store.parkingFlow.flow.initialize()
      }
    }
  }

  componentWillUnmount() {
    if (process.browser) {
      // In the browser lets track resize events to detect if we should show mobile pages.
      window.removeEventListener("resize", this.windowResizeHandler)
    }
  }

  windowResizeHandler = () => {
    if (process.browser) {
      // Update the store in the browser with boolean isMobile
      const isSmallDesktop = window.innerWidth <= SMALL_DESKTOP_SIZE
      this.store.ui.setMobile(window.innerWidth <= MOBILE_SIZE)
      this.store.ui.setTablet(window.innerWidth <= TABLET_SIZE)
      this.store.ui.setSmallDesktop(isSmallDesktop)

      if (!isSmallDesktop) {
        this.store.ui.setIsMobileMenuOpen(false)
      }
    }
  }

  handleUtmParameters = (isLoggedIn) => {
    // The ad params live on the landing URL only, so resolveAttribution reads them off
    // window.location (the router query is still empty on statically optimized pages at
    // mount) and falls back to the copy kept in local storage from an earlier load.
    // Without that fallback a reload dropped the gclid and the lookup flow saved the
    // contact info without it.
    const {
      utm: utmParameters,
      gclid,
      msclkid,
      fbclid,
      savedAt: attributionSavedAt,
    } = resolveAttribution(this.store.router.query)
    const hasUtm = Boolean(utmParameters)
    // Snapshot what the user already carries: the attribution now survives reloads, so
    // only the pieces that actually changed are worth writing back.
    const previous = isLoggedIn
      ? {
          utm: this.store.auth.user.utm,
          gclid: this.store.auth.user.gclid,
          msclkid: this.store.auth.user.msclkid,
        }
      : {}
    // Only clear competing auth-store fields on a *URL* click this load — not when
    // resolveAttribution merely restored a 90-day localStorage click on an organic visit.
    const fromUrl = collectAttributionFromQuery({
      ...this.store.router.query,
      ...readQueryFromLocation(),
    })
    const urlHasClickId = Boolean(fromUrl.gclid || fromUrl.msclkid || fromUrl.fbclid)
    if (urlHasClickId) {
      if (!fromUrl.gclid) {
        this.store.auth.setGclid('')
        if (isLoggedIn) this.store.auth.user.setGclid('')
      }
      if (!fromUrl.msclkid) {
        this.store.auth.setMsclkid('')
        if (isLoggedIn) this.store.auth.user.setMsclkid('')
      }
      if (!fromUrl.fbclid) {
        // fbclid is session-only on auth (no Parse User / auth.user.setFbclid).
        this.store.auth.setFbclid('')
      }
      // Cross-network only: a Meta click without Google click id drops stale Google UTMs.
      // Do not wipe UTMs on gclid-only auto-tag landings (Google often omits utm_*).
      const isMetaOnlyClick = Boolean(fromUrl.fbclid && !fromUrl.gclid && !fromUrl.msclkid)
      if (isMetaOnlyClick && !fromUrl.utm) {
        this.store.auth.setUtmParameters({})
        if (isLoggedIn) this.store.auth.user.setUtmParameters({})
      }
    }
    if (hasUtm) {
      if (isLoggedIn) {
        this.store.auth.user.setUtmParameters(utmParameters)
      } else {
        this.store.auth.setUtmParameters(utmParameters)
      }
    }
    // Capture GCLID (Google Click ID) from Google Ads. Always keep it on the auth
    // store, logged in or not: the parking payment sends it from there, and reading
    // it back off the User would tie the payment's attribution to a copy that only
    // exists once the visitor has an account.
    if (gclid) {
      this.store.auth.setGclid(gclid, attributionSavedAt)
      if (isLoggedIn) {
        this.store.auth.user.setGclid(gclid)
      }
    }
    // Capture FBCLID (Meta click id) for LKN Close stamping. Session-only on the
    // auth store — same localStorage window as gclid, no Parse User write.
    if (fbclid) {
      this.store.auth.setFbclid(fbclid)
    }
    // Capture MSCLKID (Microsoft Click ID) from Microsoft Advertising
    if (msclkid) {
      if (isLoggedIn) {
        this.store.auth.user.setMsclkid(msclkid)
      } else {
        this.store.auth.setMsclkid(msclkid)
      }
    }
    // If logged in and the attribution is new to the user, persist it in one update
    if (isLoggedIn) {
      const updateKeys = [
        ...(hasUtm && !isSameUtm(previous.utm, utmParameters) ? ['utm'] : []),
        ...(gclid && previous.gclid !== gclid ? ['gclid'] : []),
        ...(msclkid && previous.msclkid !== msclkid ? ['msclkid'] : []),
      ]
      if (updateKeys.length) {
        this.store.auth.user.updateUser(updateKeys, getLanding())
      }
    }
  }

  render() {
    const { Component, pageProps, pathname, messages, isMobileTrafficPayFlow, host, seoPath } = this.props
    const { currentHost } = this.store.ui
    const { router, auth, parkingFlow} = this.store
    const isMobileParkingPayFlow = parkingFlow.flow.isPayParkingFromMobile

    let contentHash = "me7ezhmogwfdkr5vsbbdt5t2nqnw21"
    if (currentHost.includes("paynyctickets.com"))
      contentHash = "zika51nmv4qd4kf1lb9dnxondbrvnd"
    if (currentHost.includes("trafficviolations.info"))
      contentHash = "jf7dm9w79xionin1agulqv33gyeeq9"

    // Meta Pixel only on trafficviolations.info (not appwinit.com / paynyctickets.com)
    const isTrafficViolations = currentHost.includes("trafficviolations.info")

    // router isn't available server-side, so use pathname from context object:
    const currentPath = pathname || router?.pathname

    // Self-reference canonical + og:url to the request host so each branded
    // domain points at itself, not the hardcoded appwinit.com domain.
    const origin = host ? `https://${host}` : ""
    const path = seoPath || "/"
    const dynamicSeo = origin
      ? {
          ...SEO,
          canonical: `${origin}${path}`,
          openGraph: { ...SEO.openGraph, url: `${origin}${path}` },
        }
      : SEO

    // Don't render anything sensitive if the user is blocked:
    let ComponentToRender = null
    if (auth.isUserBlocked && currentPath !== "/contact") {
      ComponentToRender = <IndexComponent />
    } else {
      ComponentToRender = <Component {...pageProps} />
    }

    return (
      <NextIntlClientProvider
        locale={this.props.router.locale}
        messages={messages}
      >
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
            key="viewport"
          />
          <meta name="facebook-domain-verification" content={contentHash} />
          {isTrafficViolations && (
            <script
              key="meta-pixel"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(){
                    function loadPixel(){
                      if (window._winitMetaPixelLoaded) return;
                      window._winitMetaPixelLoaded = true;
                      !function(f,b,e,v,n,t,s)
                      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                      n.queue=[];t=b.createElement(e);t.async=!0;
                      t.src=v;s=b.getElementsByTagName(e)[0];
                      s.parentNode.insertBefore(t,s)}(window,document,'script',
                      'https://connect.facebook.net/en_US/fbevents.js');
                      fbq('init', '969888049195580');
                      fbq('track', 'PageView');
                    }
                    function maybeLoad(){
                      if (window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.marketing) {
                        loadPixel();
                      }
                    }
                    maybeLoad();
                    window.addEventListener('CookiebotOnAccept', maybeLoad);
                  })();
                `,
              }}
            />
          )}
        </Head>
        <CookieBot />
        <Clarity userId={auth.user.userId} />
        <DefaultSeo {...dynamicSeo} />
        <Provider store={this.store}>
          <ThemeProvider theme={theme}>
            <>
              <UserMissingInfoModal />
              <PhoneVerificationModal />
              <BlockedModal />
              <DeletedModal />
              <MagicLinkHandler />
              <AuthReturnStateHandler />
              <GoogleOneTap />
              <Intercom />
              <GlobalStyle />
              <Toasts />
              {ComponentToRender}
            </>
          </ThemeProvider>
        </Provider>
      </NextIntlClientProvider>
    )
  }
}

export default MyApp
